import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Star, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import type { VendorItem } from '@/types/kitchenFlowPurchasing'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { cn } from '@/lib/utils'
import { PURCHASING_STRINGS } from './purchasingStrings'
import { mergeDrafts } from './purchasingPilotLogic'

const S = PURCHASING_STRINGS.catalogue

interface RowDraft { sku: string; packLabel: string; packQty: string; price: string; preferred: boolean }

const toDraft = (v: VendorItem): RowDraft => ({
  sku: v.vendor_sku ?? '', packLabel: v.pack_label ?? '', packQty: v.pack_qty != null ? String(v.pack_qty) : '',
  price: v.last_price != null ? String(v.last_price) : '', preferred: v.is_preferred,
})

/** Kitchen flow lane D: the vendor's catalogue on their profile. Items land
 *  here automatically on every costed receipt; staff add packs, their code
 *  and the main-vendor star. Renders nothing unless the kitchen flow is on. */
export function VendorCatalogSection({ supplierId, showToast }: {
  supplierId: string
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const enabled = useKitchenFlowEnabled()
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const { data, isError, refetch } = useQuery({
    queryKey: ['vendorItems', supplierId],
    queryFn: () => apiClient.listVendorItems(supplierId).then((r) => r.data ?? []),
    // Always fetch fresh when shown: the app-wide 5-minute staleTime would
    // otherwise show a list from before the last receipt / count / issue.
    staleTime: 0,
    enabled: enabled && !!supplierId,
  })
  const items = data ?? []
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({})
  // Pilot Batch 5: a refetch (e.g. after saving another row) refreshes only
  // the rows nobody is editing — unsaved edits on other rows survive.
  const lastServer = useRef<Record<string, RowDraft>>({})
  useEffect(() => {
    if (!data) return
    const fresh = Object.fromEntries(data.map((v) => [v.stock_item_id, toDraft(v)]))
    setDrafts((cur) => mergeDrafts(cur, lastServer.current, fresh, (a, b) => JSON.stringify(a) === JSON.stringify(b)))
    lastServer.current = fresh
  }, [data])

  const [search, setSearch] = useState('')
  const { data: found } = useQuery({
    queryKey: ['stockItems', { search, per_page: 8, for: 'vendorCatalogue' }],
    queryFn: () => apiClient.getStockItems({ search, per_page: 8 }).then((r) => r.data ?? []),
    enabled: enabled && search.trim().length >= 2,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['vendorItems'] })
    qc.invalidateQueries({ queryKey: ['orderSuggestions'] })
  }
  const upsert = useMutation({
    mutationFn: ({ stockItemId, d }: { stockItemId: string; d: RowDraft }) =>
      apiClient.upsertVendorItem(supplierId, {
        stock_item_id: stockItemId,
        vendor_sku: d.sku.trim() || null,
        pack_label: d.packLabel.trim() || null,
        pack_qty: d.packQty.trim() ? Number(d.packQty) : null,
        last_price: d.price.trim() ? Number(d.price) : null,
        // Pilot Batch 5: a blanked price is cleared (a null alone keeps the old one).
        clear_last_price: !d.price.trim(),
        is_preferred: d.preferred,
      }),
    onSuccess: () => { invalidate(); showToast('success', 'Saved') },
    onError: (e: Error) => showToast('error', e.message),
  })
  const remove = useMutation({
    mutationFn: (id: string) => apiClient.deleteVendorItem(id),
    onSuccess: invalidate,
    onError: (e: Error) => showToast('error', e.message),
  })

  if (!enabled) return null
  const setD = (id: string, patch: Partial<RowDraft>) => setDrafts((x) => ({ ...x, [id]: { ...x[id], ...patch } }))
  const already = new Set(items.map((i) => i.stock_item_id))

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">{S.title}</h3>
        <p className="text-sm text-muted-foreground">{S.help}</p>
      </div>
      {isError && items.length === 0 ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border p-4 text-sm">
          <span className="text-danger-fg">{PURCHASING_STRINGS.errors.load}</span>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>{PURCHASING_STRINGS.errors.retry}</Button>
        </div>
      ) : items.length === 0 && <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{S.empty}</p>}
      <ul className="space-y-2">
        {items.map((v) => {
          const d = drafts[v.stock_item_id] ?? toDraft(v)
          const orig = toDraft(v)
          const dirty = JSON.stringify(d) !== JSON.stringify(orig)
          const bad = (d.packQty.trim() && !(Number(d.packQty) > 0)) || (d.price.trim() && !(Number(d.price) >= 0))
          return (
            <li key={v.id} className="rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">{v.item_name} <span className="text-sm font-normal text-muted-foreground">({v.unit})</span></span>
                <div className="flex items-center gap-1">
                  <Button
                    type="button" variant="ghost" className={cn('px-3', d.preferred && 'text-warning-fg')}
                    aria-pressed={d.preferred} title={S.preferred}
                    onClick={() => setD(v.stock_item_id, { preferred: !d.preferred })}
                  >
                    <Star className={cn('mr-1 h-4 w-4', d.preferred && 'fill-current')} /> {S.preferred}
                  </Button>
                  <Button type="button" variant="ghost" className="h-10 w-10 p-0" aria-label={S.remove} onClick={() => remove.mutate(v.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <label className="text-xs text-muted-foreground">{S.pack}
                  <Input placeholder="Bag 25 kg" value={d.packLabel} onChange={(e) => setD(v.stock_item_id, { packLabel: e.target.value })} />
                </label>
                <label className="text-xs text-muted-foreground">{S.packQty} ({v.unit})
                  <Input inputMode="decimal" className="tabular-nums" value={d.packQty} onChange={(e) => setD(v.stock_item_id, { packQty: e.target.value })} />
                </label>
                <label className="text-xs text-muted-foreground">{S.price} / {v.unit}
                  <Input inputMode="decimal" className="tabular-nums" value={d.price} onChange={(e) => setD(v.stock_item_id, { price: e.target.value })} />
                </label>
                <label className="text-xs text-muted-foreground">{S.sku}
                  <Input value={d.sku} onChange={(e) => setD(v.stock_item_id, { sku: e.target.value })} />
                </label>
              </div>
              {d.packQty && Number(d.packQty) > 0 && d.price && Number(d.price) >= 0 && (
                <p className="mt-1 text-xs text-muted-foreground tabular-nums">1 pack ≈ {formatCurrency(Number(d.packQty) * Number(d.price))}</p>
              )}
              {dirty && (
                <div className="mt-2 flex justify-end">
                  <Button disabled={!!bad || upsert.isPending} onClick={() => upsert.mutate({ stockItemId: v.stock_item_id, d })}>
                    {S.save}
                  </Button>
                </div>
              )}
            </li>
          )
        })}
      </ul>
      <div className="space-y-2">
        <Input placeholder={`${S.add} — type a name`} value={search} onChange={(e) => setSearch(e.target.value)} />
        {(found ?? []).filter((f) => !already.has(f.id)).map((f) => (
          <button
            key={f.id} type="button"
            className="flex min-h-10 w-full items-center justify-between rounded-lg border px-3 text-left text-sm hover:bg-muted/40"
            onClick={() => {
              upsert.mutate({ stockItemId: f.id, d: { sku: '', packLabel: '', packQty: '', price: '', preferred: false } })
              setSearch('')
            }}
          >
            <span>{f.name}</span>
            <span className="text-sm text-muted-foreground">+ {S.add}</span>
          </button>
        ))}
      </div>
    </section>
  )
}
